import React from 'react';
import formatPrice, { formatCurrency } from 'utils/formatPrice';

export type RenewalPriceSize = 'lg' | 'md' | 'inline';

export interface RenewalPriceProps {
  price: number;
  // Shown struck through. Falsy means no discount applies, so nothing is rendered.
  wasPrice?: number | null | false;
  // Billing suffix such as "/month" or "/year". Empty when the period isn't annual or monthly.
  period?: string;
  // lg: basket and cover totals. md: add-on and plan options. inline: plain "£10" text in a list.
  size?: RenewalPriceSize;
  // Visible prefix for the was price, e.g. "Was". Screen readers get "was" when there is none.
  wasLabel?: string;
}

/**
 * The single place the renewal journey renders a price. Renders inline elements with no wrapper
 * so each caller keeps control of its own layout and spacing. Formatting comes from formatPrice,
 * which drops a trailing ".00".
 */
const RenewalPrice = ({
  price, wasPrice = null, period = '', size = 'md', wasLabel,
}: RenewalPriceProps) => {
  if (size === 'inline') {
    return <span>{formatCurrency(price)}</span>;
  }

  const wasText = wasPrice ? formatCurrency(wasPrice) : null;

  return (
    <>
      <sup className="fs-6">£</sup>
      <span className={`${size === 'lg' ? 'fs-3' : 'fs-4'} fw-bold px-1`}>{formatPrice(price)}</span>
      {period && <small>{period}</small>}
      {wasText && (
        <span className="fs-5 text-muted opacity-50 px-1">
          {wasLabel ? `${wasLabel} ` : <span className="visually-hidden">was </span>}
          <s>{wasText}</s>
        </span>
      )}
    </>
  );
};

export default RenewalPrice;
